import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import styled from 'styled-components';

const Section = styled(Box)`
  padding: 3rem 0;
`;

const ContentPaper = styled(Paper)`
  padding: 2rem;
  margin-top: 1.5rem;
`;

const StyledList = styled(List)`
  padding-left: 1rem;
  margin: 0.5rem 0 1.5rem;
`;

const StyledListItem = styled(ListItem)`
  padding: 0.25rem 0;
  display: list-item;
  list-style-type: disc;
`;

const SectionTitle = styled(Typography)`
  margin: 2rem 0 1rem;
  &:first-of-type {
    margin-top: 1rem;
  }
`;

const Privacy: React.FC = () => {
  return (
    <Container maxWidth="lg">
      <Section>
        <Typography variant="h3" align="center" gutterBottom>
          Privacy Policy
        </Typography>
        <Typography variant="subtitle1" align="center" color="textSecondary" gutterBottom>
          Last Updated: March 2024
        </Typography>

        <ContentPaper elevation={1}>
          <Typography variant="body1">
            At XCH.live, we take your privacy seriously. This Privacy Policy explains how we collect,
            use, and protect your personal information when you use our blockchain monitoring service.
          </Typography>

          <SectionTitle variant="h5">1. Information We Collect</SectionTitle>
          <Typography variant="body1">We collect the following types of information:</Typography>
          <StyledList>
            <StyledListItem>
              <ListItemText primary="Email address for account creation and communication" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Public wallet addresses for monitoring purposes" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Usage data and analytics about how you interact with our service" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Payment information when you subscribe to our service" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Technical data such as IP address and browser information" />
            </StyledListItem>
          </StyledList>

          <SectionTitle variant="h5">2. How We Use Your Information</SectionTitle>
          <Typography variant="body1">Your information is used for:</Typography>
          <StyledList>
            <StyledListItem>
              <ListItemText primary="Providing our wallet monitoring service" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Sending notifications about wallet activity" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Processing payments and maintaining your subscription" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Improving our service and user experience" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Communicating important updates and changes" />
            </StyledListItem>
          </StyledList>

          <SectionTitle variant="h5">3. Data Security</SectionTitle>
          <Typography variant="body1">We implement strong security measures to protect your information:</Typography>
          <StyledList>
            <StyledListItem>
              <ListItemText primary="Encryption of sensitive data in transit and at rest" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Regular security audits and penetration testing" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Strict access controls and authentication measures" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Regular backups and disaster recovery procedures" />
            </StyledListItem>
          </StyledList>

          <SectionTitle variant="h5">4. Data Sharing</SectionTitle>
          <Typography variant="body1">We do not sell your personal information. We may share data with:</Typography>
          <StyledList>
            <StyledListItem>
              <ListItemText primary="Service providers who assist in operating our platform" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Payment processors for handling transactions" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Law enforcement when required by law" />
            </StyledListItem>
          </StyledList>

          <SectionTitle variant="h5">5. Your Rights</SectionTitle>
          <Typography variant="body1">You have the right to:</Typography>
          <StyledList>
            <StyledListItem>
              <ListItemText primary="Access your personal information" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Correct inaccurate information" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Request deletion of your information" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Opt-out of marketing communications" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Export your data in a portable format" />
            </StyledListItem>
          </StyledList>

          <SectionTitle variant="h5">6. Cookies and Tracking</SectionTitle>
          <Typography variant="body1">We use cookies and similar tracking technologies to:</Typography>
          <StyledList>
            <StyledListItem>
              <ListItemText primary="Remember your preferences and settings" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Analyze how you use our service" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Improve our platform's performance" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Provide a better user experience" />
            </StyledListItem>
          </StyledList>

          <SectionTitle variant="h5">7. Google Analytics</SectionTitle>
          <Typography variant="body1">We use Google Analytics to understand how users interact with our platform:</Typography>
          <StyledList>
            <StyledListItem>
              <ListItemText primary="Collection of usage data including page views, clicks, and navigation patterns" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Technical information such as browser type, device type, and operating system" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Interaction tracking for features like wallet monitoring and settings changes" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="Performance metrics and error tracking to improve service reliability" />
            </StyledListItem>
          </StyledList>
          
          <Typography variant="body1" sx={{ mt: 2 }}>Google Analytics Data Usage:</Typography>
          <StyledList>
            <StyledListItem>
              <ListItemText primary="Data is processed by Google Analytics in accordance with their privacy policy" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="We do not allow Google to use or share our analytics data for any other purposes" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="IP addresses are anonymized and we do not track personally identifiable information through analytics" />
            </StyledListItem>
          </StyledList>

          <Typography variant="body1" sx={{ mt: 2 }}>Opting Out:</Typography>
          <StyledList>
            <StyledListItem>
              <ListItemText primary="You can opt-out of Google Analytics tracking by installing the Google Analytics Opt-out Browser Add-on" />
            </StyledListItem>
            <StyledListItem>
              <ListItemText primary="You can also disable cookies through your browser settings" />
            </StyledListItem>
          </StyledList>

          <Divider sx={{ my: 4 }} />

          <SectionTitle variant="h5">Contact Us</SectionTitle>
          <Typography variant="body1" sx={{ mb: 3 }}>
            If you have any questions about this Privacy Policy, please contact us at{' '}
            <a href="mailto:hello@xch.live">hello@xch.live</a>
          </Typography>

          <Divider />

          <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 3 }}>
            By using XCH.live, you acknowledge that you have read and understood this Privacy Policy
            and agree to the collection and use of information as described herein.
          </Typography>
        </ContentPaper>
      </Section>
    </Container>
  );
};

export default Privacy; 